import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


const WATCHED_ASPSPS = [
  { name: 'BNP Paribas', key: 'bnp_paribas' },
  { name: 'Banque BCP', key: 'banque_bcp' },
  { name: 'Revolut', key: 'revolut' },
];


function WatchBadge({ name, found }) {
  return (
    <div className="flex items-center gap-2">
      {found ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
      ) : (
        <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
      )}
      <span className="text-sm">{name}</span>
      <Badge
        variant={found ? 'default' : 'outline'}
        className={found ? 'bg-green-600 hover:bg-green-700 text-[10px]' : 'text-[10px] text-muted-foreground'}
      >
        {found ? 'found' : 'absent'}
      </Badge>
    </div>
  );
}


export function EBCoveragePanel() {
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState('FR');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  const checkCoverage = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/eb-aspsp-coverage', {
        params: { country },
      });
      setResult(data);
      toast.success(`Found ${data.aspsps?.length ?? 0} ASPSPs for ${country}`);
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Determine which watched ASPSPs are present in results
  const watchStatus = WATCHED_ASPSPS.map((w) => {
    const found = result?.aspsps?.some(
      (a) => a.name?.toLowerCase().includes(w.key.replace('_', ' '))
    ) ?? false;
    return { ...w, found };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Enable Banking ASPSP Coverage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Query available ASPSPs (banks) from Enable Banking for a given country.
          Verifies coverage for target institutions.
        </p>

        {/* Country selector + check button */}
        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Country</label>
            <select
              className="border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              <option value="FR">France (FR)</option>
              <option value="LT">Lithuania (LT)</option>
              <option value="PT">Portugal (PT)</option>
            </select>
          </div>

          <div className="pt-4">
            <Button
              size="sm"
              onClick={checkCoverage}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Check Coverage
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Querying ASPSP coverage for {country}...
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Results */}
        {!loading && result && (
          <div className="space-y-4">
            {/* Watch section */}
            <div className="border rounded-lg p-3 space-y-2 bg-muted/20">
              <p className="text-xs font-medium text-muted-foreground">Watched Institutions</p>
              {watchStatus.map((w) => (
                <WatchBadge key={w.key} name={w.name} found={w.found} />
              ))}
            </div>

            {/* Total count */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Total ASPSPs:</span>
              <Badge variant="outline" className="text-xs tabular-nums">
                {result.aspsps?.length ?? 0}
              </Badge>
            </div>

            {/* Search filter */}
            {result.aspsps?.length > 0 && (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Search ASPSPs (e.g. revolut, bnp)..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-xs w-full bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {searchFilter && (
                  <p className="text-[10px] text-muted-foreground">
                    Showing {result.aspsps.filter(a => a.name?.toLowerCase().includes(searchFilter.toLowerCase())).length} of {result.aspsps.length} ASPSPs
                  </p>
                )}
              </div>
            )}

            {/* ASPSP table */}
            {result.aspsps?.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-1.5 font-medium">ASPSP Name</th>
                      <th className="text-left px-3 py-1.5 font-medium">Country</th>
                      <th className="text-left px-3 py-1.5 font-medium">PSU Types</th>
                      <th className="text-left px-3 py-1.5 font-medium">Max Consent Validity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.aspsps
                      .filter(aspsp => !searchFilter || aspsp.name?.toLowerCase().includes(searchFilter.toLowerCase()))
                      .map((aspsp, idx) => (
                      <tr key={aspsp.name || idx} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-medium">{aspsp.name}</td>
                        <td className="px-3 py-1.5">{aspsp.country}</td>
                        <td className="px-3 py-1.5">
                          {Array.isArray(aspsp.psu_types)
                            ? aspsp.psu_types.join(', ')
                            : aspsp.psu_types || '-'}
                        </td>
                        <td className="px-3 py-1.5 tabular-nums">
                          {aspsp.max_consent_validity ?? '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.checked_at && (
              <p className="text-[10px] text-muted-foreground text-right">
                Checked at {new Date(result.checked_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
